import { useState } from 'react';
import { Group, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  BOS_KKD_LISTE_SUZGECI,
  etkinKkdSuzgeciSayisi,
  useKkdZimmetListesi,
  useLookup,
  useWorkplacesTumu,
  type KkdZimmetListeSuzgeci,
} from '../api/queries';
import { tarihGoster, tamSayiGoster } from '../lib/format';
import { Rozet, Sayfalama, SuzgecGostergesi } from '../sozlesme';
import { ANLAM } from '../theme';
import { BosListeNotu, QueryStatus } from './parts';

/**
 * KKD ZİMMET LİSTESİ — Çalışanlar modülünün ÜÇÜNCÜ sekmesi (Kapsam §30/8;
 * görevlendirme sekmesi deseni — menüde satır ARTMAZ).
 *
 * > *"Kimde ne var, kâğıdı imzalı mı?"* — kişi kişi kart açarak aranmaz.
 *
 * ⚠️ **P4:** sunucuda süzülür ve sayfalanır; süzgeç değişince sayfa 1.
 * Bahçe kişi ekseninden (sunucuda — ekran ikinci filtre yazmaz). Yalnız
 * aktif (iptal edilmemiş) satırlar (D9).
 */
const SAYFA_BOYUTU = 25;

export function KkdZimmetleriScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const isyerleri = useWorkplacesTumu();
  const kalemler = useLookup('kkd_kalemi');
  const [suzgec, setSuzgec] = useState<KkdZimmetListeSuzgeci>(BOS_KKD_LISTE_SUZGECI);
  const liste = useKkdZimmetListesi(suzgec);

  function suzgeciDegistir(yama: Partial<KkdZimmetListeSuzgeci>): void {
    setSuzgec((eski) => ({ ...eski, ...yama, sayfa: 1 }));
  }

  const satirlar = liste.data?.satirlar ?? [];
  const toplam = liste.data?.toplam ?? 0;

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={4}>KKD zimmetleri</Title>
          <Text size="xs" c="dimmed">
            Kime hangi koruyucu donanım teslim edildi ve imzalı nüshası var mı. Kayıt kişinin
            kartından açılır.
          </Text>
        </Stack>
        <Group align="flex-end">
          <Select
            data-testid="kkd-isyeri-suzgeci"
            label="İşyeri"
            placeholder="Tümü"
            clearable
            searchable
            w={220}
            value={suzgec.workplaceId}
            onChange={(deger) => suzgeciDegistir({ workplaceId: deger })}
            data={(isyerleri.data ?? []).map((w) => ({ value: w.id, label: w.ad }))}
          />
          <Select
            data-testid="kkd-kalem-suzgeci"
            label="KKD kalemi"
            placeholder="Tümü"
            clearable
            searchable
            w={220}
            value={suzgec.kalemId}
            onChange={(deger) => suzgeciDegistir({ kalemId: deger })}
            data={(kalemler.data ?? []).map((k) => ({ value: k.id, label: k.ad }))}
          />
          <TextInput
            data-testid="kkd-ara-suzgeci"
            label="Ad"
            placeholder="Ad Soyad"
            w={200}
            value={suzgec.ara}
            onChange={(e) => suzgeciDegistir({ ara: e.currentTarget.value })}
          />
        </Group>
      </Group>

      <QueryStatus queries={[isyerleri, kalemler, liste]} />
      <SuzgecGostergesi
        etkinSayisi={etkinKkdSuzgeciSayisi(suzgec)}
        onTemizle={() => setSuzgec(BOS_KKD_LISTE_SUZGECI)}
      />

      {liste.isSuccess && toplam === 0 && (
        <BosListeNotu
          mesaj={
            etkinKkdSuzgeciSayisi(suzgec) > 0
              ? 'Bu süzgeçlerle eşleşen zimmet kaydı yok — süzgeçleri temizleyebilirsiniz.'
              : 'Bahçenizde KKD zimmet kaydı yok. Zimmet kişinin kartından açılır.'
          }
        />
      )}

      {toplam > 0 && (
        <>
          <Table striped highlightOnHover data-testid="kkd-listesi">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Kişi</Table.Th>
                <Table.Th>İşyeri</Table.Th>
                <Table.Th>KKD kalemi</Table.Th>
                <Table.Th w={80}>Adet</Table.Th>
                <Table.Th w={130}>Teslim tarihi</Table.Th>
                <Table.Th>Teslim eden</Table.Th>
                <Table.Th w={180}>İmzalı nüsha</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {satirlar.map((s) => (
                <Table.Tr key={s.id} data-testid="kkd-liste-satiri">
                  <Table.Td>
                    {/* P2 — satırın birincil eylemi kaydı AÇMAKTIR; kayıt kişinin kartıdır. */}
                    <Text
                      component="a"
                      href={`/calisanlar/${s.personId}`}
                      c={ANLAM.bilgi}
                      style={{ cursor: 'pointer' }}
                      onClick={(olay) => {
                        olay.preventDefault();
                        navigate(`/calisanlar/${s.personId}`);
                      }}
                      data-testid="kkd-kisi-baglantisi"
                    >
                      {s.personAdSoyad}
                    </Text>
                  </Table.Td>
                  <Table.Td>{s.workplaceAd ?? '—'}</Table.Td>
                  <Table.Td>{s.kalemAd}</Table.Td>
                  <Table.Td>{tamSayiGoster(s.adet)}</Table.Td>
                  <Table.Td>{tarihGoster(s.teslimTarihi)}</Table.Td>
                  <Table.Td>{s.teslimEdenEtiket}</Table.Td>
                  <Table.Td>
                    {s.imzaliNusha ? (
                      <Rozet anlam="olumlu" testId="kkd-liste-imzali">
                        İmzalı
                      </Rozet>
                    ) : (
                      <Rozet anlam="dikkat" testId="kkd-liste-bekliyor">
                        Nüsha bekliyor
                      </Rozet>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Sayfalama
            sayfa={suzgec.sayfa}
            sayfaBoyutu={SAYFA_BOYUTU}
            toplam={toplam}
            onDegis={(sayfa) => setSuzgec((eski) => ({ ...eski, sayfa }))}
            testId="kkd-sayfalama"
          />
        </>
      )}
    </Stack>
  );
}
